import React, { useState } from 'react';
import { ArrowDownRightIcon, BanIcon, CheckIcon, MailIcon, MessageSquareIcon, PhoneIcon, SendIcon, UndoIcon } from 'lucide-react';
import { HotLead, TouchChannel } from '../../types/hotLead';
import { formatCurrency, formatRate, formatRelativeTime, formatShortDate } from '../../utils/format';
import { OverrideForm, ReviewIntent } from './OverrideForm';
import { ReviewBadge } from './ReviewBadge';
interface LeadDetailPanelProps {
  lead: HotLead | null;
  onAgree: (id: string) => void;
  onOverride: (id: string, code: string, subcode: string) => void;
  onDemote: (id: string, code: string, subcode: string) => void;
  onDnq: (id: string, code: string, subcode: string) => void;
}
const channelIcons: Record<TouchChannel, React.ElementType> = {
  Call: PhoneIcon,
  SMS: MessageSquareIcon,
  Email: MailIcon,
  'Direct Mail': SendIcon
};
export function LeadDetailPanel({
  lead,
  onAgree,
  onOverride,
  onDemote,
  onDnq
}: LeadDetailPanelProps) {
  const [intent, setIntent] = useState<ReviewIntent | null>(null);
  if (!lead) {
    return <aside className="flex h-full items-center justify-center rounded border border-slate-200 bg-white p-6 text-center" data-id="element-85" data-mp="3qvtql8w3wlsb">
        <p className="text-sm text-slate-500" data-id="element-86" data-mp="3msxc9udlnbu6">
          Select a lead to see its touch history and review actions.
        </p>
      </aside>;
  }
  const connectedTouches = lead.touches.filter(touch => touch.connected).length;
  const isPending = lead.review.status === 'pending';
  const submit = (code: string, subcode: string) => {
    if (intent === 'override') onOverride(lead.id, code, subcode);
    if (intent === 'demote') onDemote(lead.id, code, subcode);
    if (intent === 'dnq') onDnq(lead.id, code, subcode);
    setIntent(null);
  };
  return <aside className="flex h-full flex-col overflow-hidden rounded border border-slate-200 bg-white" data-id="element-87" data-mp="2u5b7wjwjs369">
      <header className="border-b border-slate-200 px-4 py-3" data-id="element-88" data-mp="2p685z8bv6d2i">
        <div className="flex items-start justify-between gap-3" data-id="element-89" data-mp="2p6zygaa5zuok">
          <div data-id="element-90" data-mp="2fhknllvh9p4y">
            <h2 className="text-base font-semibold text-slate-900" data-id="element-91" data-mp="2fhsy5744oicz">{lead.name}</h2>
            <p className="text-xs text-slate-500" data-id="element-92" data-mp="2kiqh3lwypgfj">
              {lead.id} · flagged {formatRelativeTime(lead.flaggedAt)}
            </p>
          </div>
          <ReviewBadge review={lead.review} showReason data-id="element-93" data-mp="25msr6ldmt8br" />
        </div>

        <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 text-xs" data-id="element-94" data-mp="2ak17egf7z2ad">
          <Field label="Loan amount" value={formatCurrency(lead.loanAmount)} data-id="element-95" data-mp="2ya51nx6ippop" />
          <Field label="Estimated value" value={formatCurrency(lead.estimatedValue)} data-id="element-96" data-mp="338q29xpombdo" />
          <Field label="Current rate" value={formatRate(lead.currentRate)} data-id="element-97" data-mp="2ojahqzgqv26f" />
          <Field label="Loan type" value={lead.loanType} data-id="element-98" data-mp="2oh7q6azy10zl" />
          <Field label="Seasoning date" value={formatShortDate(lead.seasoningDate)} data-id="element-99" data-mp="2thbyhdqp6peq" />
          <Field label="Credit pull" value={formatShortDate(lead.creditPullDate)} data-id="element-100" data-mp="3h7ppr21gkk89" />
          <Field label="Stage" value={lead.stage} data-id="element-101" data-mp="3lyelitk683d7" />
          <Field label="Disposition" value={lead.disposition} data-id="element-102" data-mp="3lzzc683d725s" />
        </dl>

        <div className="mt-3 flex flex-wrap gap-1.5" data-id="element-103" data-mp="1aiebvkuqp622">
          <Signal label="Rate threshold" active={lead.signals.rateThreshold} data-id="element-104" data-mp="15km7vsgtlthi" />
          <Signal label="Equity" active={lead.signals.equity} data-id="element-105" data-mp="15ii6bh6e96ke" />
          <Signal label="Meaningful conversation" active={lead.signals.meaningfulConversation} data-id="element-106" data-mp="1k87azmdp3spf" />
          <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[11px] font-semibold text-slate-700" data-id="element-107" data-mp="1fcijm6btxi47">
            Score {lead.score}
          </span>
        </div>
      </header>

      <div className="flex-1 overflow-y-auto px-4 py-3" data-id="element-108" data-mp="mnbc4huedi6t">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500" data-id="element-109" data-mp="10m18twdb1fun">
          Touch history
        </h3>
        <p className="mt-0.5 text-[11px] text-slate-500" data-id="element-110" data-mp="wiv792150cui">
          {lead.touches.length} touches · {connectedTouches} connected
        </p>

        <ol className="mt-3 space-y-3" data-id="element-111" data-mp="2c2j5nxktdkht">
          {lead.touches.map(touch => {
          const Icon = channelIcons[touch.channel];
          return <li key={touch.sequence} className="flex gap-3" data-id="element-112" data-mp="33aky1ez7h42v">
                <div className="flex flex-col items-center" data-id="element-113" data-mp="37ala4syu136r">
                  <span className={`flex h-6 w-6 items-center justify-center rounded-full border ${touch.connected ? 'border-[#acf3e4] bg-[#ebfdf7] text-[#056764]' : 'border-slate-200 bg-slate-100 text-slate-500'}`} data-id="element-114" data-mp="37cxrb3wrn9ir">
                    <Icon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-115" data-mp="3q8xmenl1it6k" />
                  </span>
                  <span className="mt-1 flex-1 border-l border-dashed border-slate-200" data-id="element-116" data-mp="3uzxkvx9t5jb6" />
                </div>
                <div className="pb-1" data-id="element-117" data-mp="3uebj0v9xw4u">
                  <p className="text-xs font-semibold text-slate-900" data-id="element-118" data-mp="3td02vacj7ml">
                    {ordinal(touch.sequence)} touch · {touch.channel}
                    {!touch.connected && <span className="ml-1.5 font-normal text-slate-500" data-id="element-119" data-mp="dstxyqjcwh80">
                        no connect
                      </span>}
                  </p>
                  <p className="text-xs text-slate-700" data-id="element-120" data-mp="1ta8juof09jd9">{touch.outcome}</p>
                  <p className="text-[11px] text-slate-500" data-id="element-121" data-mp="1obng4f0johk2">
                    {formatShortDate(touch.occurredAt)} · {touch.owner}
                  </p>
                </div>
              </li>;
        })}
        </ol>

        <div className="mt-4 rounded border border-slate-200 bg-slate-50 px-3 py-2" data-id="element-122" data-mp="2gzjbngedo3f9">
          <h4 className="text-[11px] font-semibold uppercase tracking-wide text-slate-500" data-id="element-123" data-mp="2c2svkt2kp7tu">
            Demotion &amp; DNQ rules
          </h4>
          <ul className="mt-1 space-y-1 text-[11px] text-slate-600" data-id="element-124" data-mp="278yg61u03f0k">
            <li data-id="element-125" data-mp="22bf0cxhnmu6i">· 4 touches with no connect → auto-demote to nurture</li>
            <li data-id="element-126" data-mp="2zrlsarr0rjpi">· Credit pull older than 30 days → re-pull before promoting</li>
            <li data-id="element-127" data-mp="2v0vnbfmi9ivc">· DNQ suppresses the lead from being re-flagged as hot</li>
          </ul>
        </div>
      </div>

      {intent ? <OverrideForm leadName={lead.name} intent={intent} onCancel={() => setIntent(null)} onSubmit={submit} data-id="element-128" data-mp="t4efzluq8sgf" /> : <footer className="flex flex-wrap items-center gap-2 border-t border-slate-200 bg-slate-50 px-4 py-3" data-id="element-129" data-mp="jds6889oyw5n">
          <button type="button" onClick={() => onAgree(lead.id)} disabled={!isPending} className="inline-flex items-center gap-1 rounded bg-[#0176d3] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#014486] disabled:cursor-not-allowed disabled:bg-slate-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0176d3] focus-visible:ring-offset-1" data-id="element-130" data-mp="oblagvrfpg3a">
            <CheckIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-131" data-mp="5e0xon1y27z1" />
            Agree
          </button>
          <button type="button" onClick={() => setIntent('override')} className="inline-flex items-center gap-1 rounded border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0176d3]" data-id="element-132" data-mp="1usynlcee84lv">
            <UndoIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-133" data-mp="3tzdsyiy5gob3" />
            Override
          </button>
          <button type="button" onClick={() => setIntent('demote')} className="inline-flex items-center gap-1 rounded border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0176d3]" data-id="element-134" data-mp="3p8xfiyubx2oq">
            <ArrowDownRightIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-135" data-mp="grj42t9ru69a" />
            Demote
          </button>
          <button type="button" onClick={() => setIntent('dnq')} className="inline-flex items-center gap-1 rounded border border-[#feb8b8] bg-white px-3 py-1.5 text-xs font-semibold text-[#ba0517] hover:bg-[#fef1f1] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#ba0517]" data-id="element-136" data-mp="buaisjtbtm3t">
            <BanIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-137" data-mp="1wr1mws3v91pm" />
            DNQ
          </button>
        </footer>}
    </aside>;
}
function Field({
  label,
  value
}: {
  label: string;
  value: string;
}) {
  return <div className="min-w-0" data-id="element-138" data-mp="26hcoi802uytc">
      <dt className="text-[11px] uppercase tracking-wide text-slate-500" data-id="element-139" data-mp="26fjkl0gumsp1">{label}</dt>
      <dd className="truncate font-medium text-slate-900" data-id="element-140" data-mp="18u4bz2i2103h">{value}</dd>
    </div>;
}
function Signal({
  label,
  active
}: {
  label: string;
  active: boolean;
}) {
  return <span className={active ? 'rounded border border-[#acf3e4] bg-[#ebfdf7] px-1.5 py-0.5 text-[11px] font-medium text-[#056764]' : 'rounded border border-slate-200 bg-white px-1.5 py-0.5 text-[11px] font-medium text-slate-400 line-through'} data-id="element-141" data-mp="1irid7uvepib7">
      {label}
    </span>;
}
function ordinal(value: number): string {
  const suffixes = ['th', 'st', 'nd', 'rd'];
  const remainder = value % 100;
  return `${value}${suffixes[(remainder - 20) % 10] ?? suffixes[remainder] ?? suffixes[0]}`;
}